const tags = new Set(['DIV', 'SECTION', 'H3', 'UL', 'LI', 'A', 'IMG', 'SPAN', 'STRONG', 'SMALL', 'svg', 'path']);
const attributes = new Set(['class', 'href', 'src', 'srcset', 'sizes', 'width', 'height', 'alt', 'loading', 'decoding', 'style', 'aria-hidden', 'focusable', 'fill', 'viewbox', 'd', 'stroke', 'stroke-linecap', 'data-search-result', 'data-resource-id']);
const resourcePaths = { product: /\/products\/[^/]+/, collection: /\/collections\/[^/]+/, article: /\/blogs\/[^/]+\/[^/]+/, page: /\/pages\/[^/]+/, query: /\/search\/?$/ };

export function readSearchResponse(html, query, win) {
  if (typeof html !== 'string' || html.length > 500000) throw Error('Unexpected search response');
  const parsed = new win.DOMParser().parseFromString(html, 'text/html');
  const responses = parsed.querySelectorAll('[data-raith-search-response]');
  if (responses.length !== 1) throw Error('Missing search response');
  const response = responses[0];
  if (response.dataset.searchQuery?.trim() !== query) throw Error('Mismatched search response');
  const count = Number(response.dataset.searchCount);
  if (!/^\d+$/.test(response.dataset.searchCount || '') || count > 100 || !response.dataset.searchMessage) throw Error('Invalid search metadata');
  const nativeImage = value => {
    if (typeof value !== 'string' || !value.trim()) throw Error('Missing image URL');
    const url = new win.URL(value, win.location.origin);
    if (url.protocol !== 'https:' || (url.origin !== win.location.origin && url.hostname !== 'cdn.shopify.com')) throw Error('Unexpected image URL');
  };
  for (const node of response.querySelectorAll('*')) {
    if (!tags.has(node.tagName)) throw Error('Unexpected search markup');
    for (const attr of node.attributes) {
      if (!attributes.has(attr.name.toLowerCase())) throw Error('Unexpected search attribute');
      if (attr.name === 'style' && attr.value.trim() !== '') throw Error('Unexpected inline style');
    }
    if (node.tagName === 'A') {
      const route = resourcePaths[node.dataset.searchResult];
      const url = new win.URL(node.getAttribute('href'), win.location.origin);
      if (!route || url.origin !== win.location.origin || !/^https?:$/.test(url.protocol) || !route.test(url.pathname)) throw Error('Unexpected resource URL');
    }
    if (node.tagName === 'IMG') {
      nativeImage(node.getAttribute('src'));
      for (const candidate of (node.getAttribute('srcset') || '').split(',').filter(Boolean)) {
        const [url, descriptor] = candidate.trim().split(/\s+/);
        if (!/^\d+(?:\.\d+)?[wx]$/.test(descriptor || '')) throw Error('Unexpected image descriptor');
        nativeImage(url);
      }
    }
  }
  if (response.querySelectorAll('a[data-search-result]').length !== count) throw Error('Incomplete search response');
  return { content: response, message: response.dataset.searchMessage, count };
}

export function registerRaithSearch(win = window) {
  if (win.customElements.get('raith-search')) return;
  class RaithSearch extends win.HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.composing = false;
      if (!this.input || !this.results || !this.status || !this.clearButton || !this.dataset.searchLoading || !this.dataset.searchError) return;
      try {
        this.endpoint = new win.URL(this.dataset.searchEndpoint, win.location.origin);
        if (this.endpoint.origin !== win.location.origin || !/\/search\/suggest\/?$/.test(this.endpoint.pathname)) return;
      } catch { return; }
      this.listeners = new win.AbortController();
      const { signal } = this.listeners;
      this.input.addEventListener('input', event => {
        if (event.isComposing || this.composing) { this.invalidate(); return; }
        this.schedule();
      }, { signal });
      this.input.addEventListener('search', () => { if (!this.composing) this.schedule(); }, { signal });
      this.input.addEventListener('compositionstart', () => { this.composing = true; this.invalidate(); }, { signal });
      this.input.addEventListener('compositionend', () => { this.composing = false; this.schedule(); }, { signal });
      this.clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.composing = false;
        this.invalidate();
        this.clearButton.hidden = true;
        this.input.focus();
      }, { signal });
      this.closest('dialog')?.addEventListener('close', () => this.invalidate(), { signal });
      this.querySelector('form')?.addEventListener('submit', () => this.invalidate(), { signal });
      this.clearButton.hidden = !this.input.value;
    }
    disconnectedCallback() {
      this.listeners?.abort();
      this.invalidate();
    }
    invalidate() {
      this.generation = (this.generation || 0) + 1;
      win.clearTimeout(this.timer);
      this.request?.abort();
      this.request = null;
      this.results?.replaceChildren();
      this.results?.setAttribute('aria-busy', 'false');
      if (this.status) this.status.textContent = '';
    }
    schedule() {
      this.invalidate();
      this.clearButton.hidden = !this.input.value;
      const query = this.input.value.trim();
      if (!query) return;
      const generation = this.generation;
      this.timer = win.setTimeout(() => this.search(query, generation), 250);
    }
    async search(query, generation) {
      if (!this.isConnected || this.composing || generation !== this.generation) return;
      this.request = new win.AbortController();
      const url = new win.URL(this.endpoint);
      url.search = '';
      url.hash = '';
      url.searchParams.set('q', query);
      url.searchParams.set('section_id', 'raith-predictive-search');
      this.results.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.searchLoading;
      const current = () => this.isConnected && !this.composing && generation === this.generation && this.input.value.trim() === query;
      try {
        const response = await win.fetch(url, { signal: this.request.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!current()) return;
        if (!response.ok) throw Error('Search unavailable');
        const html = await response.text();
        if (!current()) return;
        const result = readSearchResponse(html, query, win);
        this.results.replaceChildren(...win.document.importNode(result.content, true).childNodes);
        this.status.textContent = result.message;
      } catch {
        if (current()) {
          this.results.replaceChildren();
          this.status.textContent = this.dataset.searchError;
        }
      } finally {
        if (current()) this.results.setAttribute('aria-busy', 'false');
      }
    }
  }
  win.customElements.define('raith-search', RaithSearch);
}
if (typeof window !== 'undefined' && window.customElements) registerRaithSearch(window);
